import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  CasBaseDialog,
  CasDisplayField,
  CasLoaderWithBackdrop,
  CasStackRow,
} from 'src/compass/shared/components';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import { DateFormat, formatDateTime } from 'src/compass/shared/util/casMoment';
import HubspotLink from '../HubspotLink';

interface IValue {
  label: string;
  value?: string | null;
}

function Values({ values }: { values: IValue[] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3 }}>
      {values.map((v) => (
        <CasDisplayField key={v.label} label={v.label} value={v.value} />
      ))}
    </Box>
  );
}

/*
  One hubspot.PollingLog row: the change as the poll logged it - its After values, the record's
  synced columns as the poll left them - and, in the Current box, the record as the copy holds it
  now, with a link to it in HubSpot (the company's, for a company contact).
*/
export default function PollingLogDetailsDialog({
  id,
  onClose,
}: {
  id: number;
  onClose: VoidFunction;
}) {
  const { data, isLoading, isError } = useRetrieveDetailsById({ entity: 'Hubspot/PollingLog', id });

  if (isLoading) return <CasLoaderWithBackdrop />;

  if (isError) throw new Error('Error retrieving data from the server.');

  return (
    <CasBaseDialog open onClose={onClose} title="Hubspot Sync" showCloseIcon>
      <Typography variant="subtitle1">Event details</Typography>
      <CasStackRow>
        <CasDisplayField
          label="When"
          value={formatDateTime(data?.dateCreated, DateFormat.MDY_HMS)}
        />
        <CasDisplayField label="What" value={data?.what} />
        <CasDisplayField label="Op" value={data?.op} />
      </CasStackRow>
      <CasDisplayField label="Who" value={data?.who} />
      {data?.after?.length > 0 && <Values values={data.after} />}

      <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1">Current</Typography>
          {data?.hubspotId && (
            <HubspotLink id={data.hubspotId} recordPath={data.hubspotRecordPath} />
          )}
        </Stack>
        {data?.current?.length > 0 ? (
          <Values values={data.current} />
        ) : (
          <Typography variant="body2">Not in the copy.</Typography>
        )}
      </Box>
    </CasBaseDialog>
  );
}
